import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ChevronLeft, ChevronRight, RefreshCw, Search, Filter } from 'lucide-react';
import { Input } from './ui/input';

const OrderManagement = ({ api, user, onEditOrder, onDeleteOrder }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [facilityFilter, setFacilityFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const loadOrders = async (page = 1, refresh = false) => {
    if (loading && !refresh) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50'
      });
      
      if (statusFilter !== 'all') params.append('status_filter', statusFilter);
      if (facilityFilter !== 'all') params.append('facility_filter', facilityFilter);
      
      const response = await api.get(`/api/manager/orders?${params.toString()}`);
      
      if (response.data.success) {
        let filteredOrders = response.data.orders || [];
        
        // Apply client-side search filter
        if (searchQuery.trim()) {
          const query = searchQuery.toLowerCase().trim();
          filteredOrders = filteredOrders.filter(order => 
            order.guest_name?.toLowerCase().includes(query) ||
            order.room_number?.toLowerCase().includes(query) ||
            order.id?.toLowerCase().includes(query)
          );
        }
        
        setOrders(filteredOrders);
        setCurrentPage(response.data.page || 1);
        setTotalPages(response.data.total_pages || 1);
        setTotalCount(response.data.total_count || 0);
      }
    } catch (error) {
      console.error('Failed to load orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders(1);
  }, [statusFilter, facilityFilter]);

  useEffect(() => {
    loadOrders(currentPage);
  }, [searchQuery]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      loadOrders(newPage);
    }
  };

  const getStatusBadge = (order) => {
    const status = order.display_status || order.status;
    const statusColor = order.status_color || 'gray';
    
    // Shorten status messages for mobile
    let statusText = order.status_message || status;
    if (statusText === 'In Transit to Facility') statusText = 'In Transit';
    if (statusText === 'Awaiting Pickup by Driver') statusText = 'Awaiting';
    if (statusText === 'Ready for Pickup') statusText = 'Ready';
    
    return (
      <Badge 
        variant={statusColor === 'green' ? 'default' : statusColor === 'orange' ? 'secondary' : statusColor === 'blue' ? 'outline' : 'outline'}
        className={`text-xs whitespace-nowrap ${
          statusColor === 'green' ? 'bg-green-100 text-green-800' : 
          statusColor === 'orange' ? 'bg-orange-100 text-orange-800' :
          statusColor === 'blue' ? 'bg-blue-100 text-blue-800 border-blue-300' : ''
        }`}
      >
        {statusText}
      </Badge>
    );
  };

  return (
    <Card className="w-full">
      <CardHeader className="p-3 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <CardTitle className="flex items-center gap-2 text-base sm:text-xl">
            <span className="hidden sm:inline">Order Management</span>
            <span className="sm:hidden">Orders</span>
            <Badge variant="secondary" className="text-xs">{totalCount}</Badge>
          </CardTitle>
          <Button 
            onClick={() => loadOrders(currentPage, true)}
            disabled={loading}
            size="sm"
            className="text-xs px-2 py-1 h-7 sm:h-8 self-end sm:self-auto"
          >
            <RefreshCw className={`w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 items-stretch sm:items-center mt-2">
          <div className="flex items-center gap-2 flex-1 sm:flex-none">
            <Search className="w-4 h-4 flex-shrink-0" />
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 sm:w-32 md:w-48 h-8 text-sm"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 flex-shrink-0" />
            <select 
              value={statusFilter} 
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border rounded px-2 py-1 text-sm flex-1 sm:flex-none h-8"
            >
              <option value="all">All</option>
              <option value="ready_for_pickup">Ready</option>
              <option value="in_transit">In Transit</option>
            </select>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="p-3 sm:p-6">
        {loading && orders.length === 0 ? (
          <div className="text-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading orders...
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No orders found matching your criteria
          </div>
        ) : (
          <>
            {/* Orders List */}
            <div className="space-y-3">
              {orders.map((order) => (
                <div key={order.id} className="border rounded-lg p-3 sm:p-4 hover:bg-gray-50">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-2">
                        <span className="font-medium text-sm sm:text-base truncate">{order.guest_name}</span>
                        <Badge variant="outline" className="text-xs">Room {order.room_number}</Badge>
                        <div className="w-full sm:w-auto">
                          {getStatusBadge(order)}
                        </div>
                      </div>
                      
                      <div className="text-xs sm:text-sm text-gray-600 grid grid-cols-2 gap-1 sm:gap-2">
                        <div className="truncate">ID: {order.id?.slice(-8)}</div>
                        <div>Bags: {order.bag_count || 0}</div>
                        <div className="truncate">Type: {order.order_type || 'Standard'}</div>
                        <div className="truncate">Ready: {order.ready_date ? new Date(order.ready_date).toLocaleDateString() : 'N/A'}</div>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 mt-2 sm:mt-0 sm:ml-4 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEditOrder(order)}
                        className="text-xs px-2 py-1 h-7 sm:h-8"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onDeleteOrder(order)}
                        className="text-red-600 hover:text-red-700 text-xs px-2 py-1 h-7 sm:h-8"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row justify-between items-center mt-4 sm:mt-6 pt-3 sm:pt-4 border-t gap-2">
                <div className="text-xs sm:text-sm text-gray-600">
                  Page {currentPage}/{totalPages} ({totalCount})
                </div>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1 || loading}
                    className="text-xs px-2 py-1 h-7 sm:h-8"
                  >
                    <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Previous</span>
                    <span className="sm:hidden">Prev</span>
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages || loading}
                    className="text-xs px-2 py-1 h-7 sm:h-8"
                  >
                    Next
                    <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default OrderManagement;